import "./styles.css";
import React, { useState, useEffect, useMemo } from "react";
import {
  FaUserAlt,
  FaIdCard,
  FaCheckCircle,
  FaExclamationCircle,
  FaClock,
  FaMoneyBill,
  FaTruck,
} from "react-icons/fa";
import { IoMdCheckboxOutline } from "react-icons/io";
import { PiCityBold } from "react-icons/pi";
import { TbDeviceIpadExclamation } from "react-icons/tb";
import { useNavigate } from "react-router-dom";
import { format, toZonedTime } from "date-fns-tz";
import { addHours } from "date-fns";

const ListaVisita = ({ listaVisitas = [], idmotorista, maxHeight }) => {
  const navigate = useNavigate();
  const [intervalos, setIntervalos] = useState({});

  const formatarDataHora = (datahora) => {
    if (!datahora) return "-";
    const timeZone = "America/Sao_Paulo";
    const dataZoned = toZonedTime(datahora, timeZone);
    const dataSomada = addHours(dataZoned, 0);
    const formato = "dd/MM/yyyy HH:mm:ss";
    return format(dataSomada, formato);
  };

  const getStatusMeta = (situacaovisita) => {
    switch (situacaovisita) {
      case 0:
        return { className: "status-nao-visitado", label: "Nao visitado", iconColor: "#64748b" };
      case 1:
        return { className: "status-visitado", label: "Visitado", iconColor: "#16a34a" };
      case 2:
        return { className: "status-nao-entregue", label: "Nao entregue", iconColor: "#dc2626" };
      case 3:
        return { className: "status-em-andamento", label: "Em andamento", iconColor: "#ca8a04" };
      default:
        return { className: "status-nao-visitado", label: "Nao visitado", iconColor: "#64748b" };
    }
  };

  const getFirstMovDate = (movimentos) => {
    if (!movimentos || movimentos.length === 0) return null;
    let min = null;
    for (const m of movimentos) {
      if (!m || !m.datahora) continue;
      const d = new Date(m.datahora);
      if (isNaN(d)) continue;
      if (min === null || d < min) min = d;
    }
    return min;
  };

  const getLastMovDate = (movimentos) => {
    if (!movimentos || movimentos.length === 0) return null;
    let max = null;
    for (const m of movimentos) {
      if (!m || !m.datahora) continue;
      const d = new Date(m.datahora);
      if (isNaN(d)) continue;
      if (max === null || d > max) max = d;
    }
    return max;
  };

  const sortedVisitas = useMemo(() => {
    return [...listaVisitas]
      .map((v, idx) => ({ ...v, originalIndex: idx }))
      .sort((a, b) => {
        const dataA = getFirstMovDate(a.movimentos);
        const dataB = getFirstMovDate(b.movimentos);

        if (dataA && dataB) return dataA.getTime() - dataB.getTime();
        if (dataA && !dataB) return -1;
        if (!dataA && dataB) return 1;
        return a.originalIndex - b.originalIndex;
      });
  }, [listaVisitas]);

  const calcularIntervalos = (visitasOrdenadas) => {
    const res = {};
    if (!visitasOrdenadas || visitasOrdenadas.length === 0) return res;

    for (let i = 0; i < visitasOrdenadas.length; i++) {
      const atual = visitasOrdenadas[i];
      const proximo = visitasOrdenadas[i + 1];

      if (!proximo) {
        res[atual.IDvisita] = "-";
        continue;
      }

      const ultimoAtual = getLastMovDate(atual.movimentos);
      const primeiroProximo = getFirstMovDate(proximo.movimentos);

      if (!ultimoAtual || !primeiroProximo) {
        res[atual.IDvisita] = "-";
      } else {
        const diffMs = primeiroProximo.getTime() - ultimoAtual.getTime();

        if (isNaN(diffMs) || diffMs <= 0) {
          res[atual.IDvisita] = "-";
        } else {
          const horas = Math.floor(diffMs / (1000 * 60 * 60));
          const minutos = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

          if (horas === 0 && minutos === 0) {
            res[atual.IDvisita] = "-";
          } else {
            res[atual.IDvisita] = `${String(horas).padStart(2, "0")} Horas e ${String(minutos).padStart(2, "0")} Minutos`;
          }
        }
      }
    }
    return res;
  };

  useEffect(() => {
    if (sortedVisitas.length > 0) {
      setIntervalos(calcularIntervalos(sortedVisitas));
    } else {
      setIntervalos({});
    }
  }, [sortedVisitas]);

  const handleCardClick = (IDmotorista, IDvisita) => {
    navigate(`/Visita/${IDmotorista}/${IDvisita}`);
  };

  return (
    <div
      className="card-container visita-card-container"
      style={{ maxHeight: maxHeight || "58vh", overflowY: "auto", padding: "10px" }}
    >
      {sortedVisitas.map((visita) => {
        const statusMeta = getStatusMeta(visita.situacaovisita);

        return (
          <React.Fragment key={visita.IDvisita}>
            <div
              className={`card visita-card ${statusMeta.className}`}
              onClick={() => handleCardClick(idmotorista, visita.IDvisita)}
            >
              <div className="visita-status-chip">{statusMeta.label}</div>

              <div className="card-content">
                <div className="visita-card-header">
                  <h3 className="visita-card-title">
                    <FaUserAlt style={{ marginRight: "8px" }} />
                    Cliente: {visita.IDcliente} - {visita.nomecliente}
                  </h3>
                  <div className="visita-card-meta">
                    <span className="visita-card-subtitle">Visita n: {visita.IDvisita}</span>
                    {visita.situacaovisita === 1 && (
                      <FaCheckCircle style={{ color: statusMeta.iconColor }} />
                    )}
                  </div>
                </div>

                <div className="visita-card-body">
                  <div className="visita-info-column">
                    <div className="visita-info-item">
                      <FaIdCard />
                      <span>{visita.enderecocliente}</span>
                    </div>
                    <div className="visita-info-item">
                      <PiCityBold />
                      <span>{visita.cidadecliente}</span>
                    </div>
                    <div className="visita-info-item visita-info-inline">
                      <span className="visita-info-pair">
                        <FaMoneyBill />
                        <span>R$ {visita.valor}</span>
                      </span>
                      <span className="visita-info-pair">
                        <FaMoneyBill />
                        <span>Plano Pagamento: {visita.planopag}</span>
                      </span>
                    </div>
                    {visita.situacaovisita === 1 && (
                      <div className="visita-info-item visita-time-spent">
                        <FaClock />
                        <b>Tempo Gasto: {visita.tempo} min.</b>
                      </div>
                    )}
                    <div className="visita-info-item">
                      <TbDeviceIpadExclamation />
                      <span>Obs: {visita.observacaovisita ? visita.observacaovisita : "-"}</span>
                    </div>
                  </div>

                  <div className="visita-movimentos-column">
                    {(() => {
                      const mov1 = visita.movimentos?.find((m) => m.tipo === 1);
                      const mov2 = visita.movimentos?.find((m) => m.tipo === 2);
                      const mov3 = visita.movimentos?.find((m) => m.tipo === 3);

                      const calcDiff = (start, end) => {
                        if (!start || !end) return "-";
                        const diffMs = new Date(end).getTime() - new Date(start).getTime();
                        if (diffMs <= 0) return "-";
                        const horas = Math.floor(diffMs / (1000 * 60 * 60));
                        const minutos = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                        return `${String(horas).padStart(2, "0")}h ${String(minutos).padStart(2, "0")}m`;
                      };

                      const tempoEntrega = calcDiff(mov2?.datahora, mov3?.datahora);
                      const tempoTotal = calcDiff(mov1?.datahora, mov3?.datahora);

                      return (
                        <>
                          {visita.movimentos?.map((mov) => (
                            <div className="visita-movimento-row" key={mov.id}>
                              <span className="visita-movimento-label">
                                {(() => {
                                  switch (mov.tipo) {
                                    case 1:
                                      return (
                                        <>
                                          <IoMdCheckboxOutline className="visita-movimento-icon" />
                                          Chegou no local.
                                        </>
                                      );
                                    case 2:
                                      return (
                                        <>
                                          <IoMdCheckboxOutline className="visita-movimento-icon" />
                                          Comecou entrega.
                                        </>
                                      );
                                    case 3:
                                      return (
                                        <>
                                          <IoMdCheckboxOutline className="visita-movimento-icon" />
                                          {/* Colheu assinatura. */}
                                          Comprovou entrega.
                                        </>
                                      );
                                    case 4:
                                      return (
                                        <>
                                          <IoMdCheckboxOutline className="visita-movimento-icon" />
                                          Finalizou entrega.
                                        </>
                                      );
                                    case 5:
                                      return (
                                        <>
                                          <FaExclamationCircle className="visita-movimento-icon visita-movimento-alerta" />
                                          Ocorrencia.
                                        </>
                                      );
                                    default:
                                      return mov.tipo;
                                  }
                                })()}
                              </span>

                              <span className="visita-movimento-date">
                                <FaIdCard style={{ marginRight: "8px" }} />
                                {formatarDataHora(mov.datahora)}
                              </span>
                            </div>
                          ))}

                          <div className="visita-tempos">
                            {tempoEntrega !== "-" && (
                              <div className="visita-tempo-item">
                                <FaClock style={{ marginRight: "5px" }} />
                                Tempo de entrega: {tempoEntrega}
                              </div>
                            )}
                            {tempoTotal !== "-" && (
                              <div className="visita-tempo-item">
                                <FaClock style={{ marginRight: "5px" }} />
                                Tempo total: {tempoTotal}
                              </div>
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>

            {intervalos[visita.IDvisita] && intervalos[visita.IDvisita] !== "-" && (
              <p className="visita-intervalo">
                <FaTruck style={{ marginRight: "8px" }} /> {intervalos[visita.IDvisita]}
              </p>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default ListaVisita;
